import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AgentList from './AgentList';
import ConversationHistory from './ConversationHistory';
import PhoneNumbers from './PhoneNumbers';
import KnowledgeBase from './KnowledgeBase';
import Settings from './Settings';

type TabType = 'agents' | 'conversations' | 'phone-numbers' | 'knowledge-base' | 'settings';

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('agents');
  const [apiKey, setApiKey] = useState<string>('');

  useEffect(() => {
    // Read API key from environment variables
    const envApiKey = process.env.REACT_APP_ELEVENLABS_API_KEY || '';
    setApiKey(envApiKey);
  }, []);

  const tabs = [
    { id: 'agents' as TabType, name: 'Agents', icon: '🤖' },
    { id: 'conversations' as TabType, name: 'Conversations', icon: '💬' },
    { id: 'phone-numbers' as TabType, name: 'Phone Numbers', icon: '📞' },
    { id: 'knowledge-base' as TabType, name: 'Knowledge Base', icon: '📚' },
    { id: 'settings' as TabType, name: 'Settings', icon: '⚙️' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'agents':
        return <AgentList apiKey={apiKey} />;
      case 'conversations':
        return <ConversationHistory apiKey={apiKey} />;
      case 'phone-numbers':
        return <PhoneNumbers apiKey={apiKey} />;
      case 'knowledge-base':
        return <KnowledgeBase apiKey={apiKey} />;
      case 'settings':
        return <Settings apiKey={apiKey} />;
      default:
        return <AgentList apiKey={apiKey} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">

      {/* Header */}
      <header className="relative z-10 glass-effect border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center hover:scale-105 transition-transform duration-300">
                <img 
                  src="crel_logo.png" 
                  alt="Crel AI" 
                  className="h-8 w-auto" 
                />
              </Link>
              <span className="ml-4 text-gray-400">AI Dashboard</span>
            </div>
            <div className="flex items-center space-x-4">
              {!apiKey && (
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-sm font-medium">
                  <div className="w-2 h-2 bg-red-400 rounded-full mr-2 animate-pulse"></div>
                  API Key Missing
                </div>
              )}
              {apiKey && (
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30 text-green-300 text-sm font-medium">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  Connected
                </div>
              )}
              <Link
                to="/"
                className="text-gray-400 hover:text-white text-sm font-medium transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* API Key Warning */}
        {!apiKey && (
          <div className="glass-card rounded-2xl p-6 mb-8 neon-border border-yellow-500/30">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-yellow-400 text-2xl">⚠️</span>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-yellow-300 mb-2">
                  API Key Missing
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Please add your API key to the <code className="bg-black/30 px-2 py-1 rounded text-yellow-200">REACT_APP_ELEVENLABS_API_KEY</code> environment variable to unlock the full potential of your AI dashboard.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-4 text-white">AI Agent Dashboard</h1>
          <p className="text-xl text-gray-300 font-light">
            Manage your <span className="font-semibold text-white">intelligent voice agents</span> with ease
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="glass-card rounded-2xl mb-8 p-2">
          {/* Desktop Navigation */}
          <nav className="hidden sm:flex space-x-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-6 rounded-xl font-medium text-sm flex items-center justify-center space-x-3 transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
          
          {/* Mobile Navigation - Horizontal Scroll */}
          <nav className="sm:hidden overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            <div className="flex space-x-2 min-w-max px-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-4 rounded-xl font-medium text-sm flex items-center space-x-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span className="text-xs">{tab.name}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Content Area */}
        <div className="glass-card rounded-2xl overflow-hidden">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 